import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  Calendar, Users, FileText, Smartphone, Zap, 
  DollarSign, Bell, BarChart3, Building2, 
  MessageSquare, Shield, CheckCircle2, Clock,
  Settings, TrendingUp, Globe, Award
} from 'lucide-react';

const FeaturesPage = () => {
  const featureCategories = [
    {
      id: 'automate',
      title: 'Automatiza tus Operaciones',
      description: 'Coloca tareas manuales y repetitivas en piloto automático para simplificar tus operaciones del día a día',
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80',
      features: [
        {
          icon: Calendar,
          title: 'Reservas Inteligentes',
          description: 'Sistema de reservas sin esfuerzo con automatización inteligente para espacios, salas y recursos',
          link: '#reservas'
        },
        {
          icon: FileText,
          title: 'Facturación Automática',
          description: 'Ahorra tiempo y reduce trabajo manual con facturación electrónica automatizada',
          link: '#facturacion'
        },
        {
          icon: DollarSign,
          title: 'Pagos y Cobranza',
          description: 'Gestión automatizada de pagos recurrentes y recordatorios inteligentes',
          link: '#pagos'
        },
        {
          icon: Users,
          title: 'CRM Avanzado',
          description: 'Gestiona relaciones con clientes desde lead hasta miembro activo',
          link: '#crm'
        },
        {
          icon: Bell,
          title: 'Notificaciones Automáticas',
          description: 'Mantén a tus usuarios informados con notificaciones inteligentes y oportunas',
          link: '#notificaciones'
        },
        {
          icon: CheckCircle2,
          title: 'Check-in Digital',
          description: 'Optimiza el proceso de check-in con código QR y control de acceso',
          link: '#checkin'
        }
      ]
    },
    {
      id: 'engage',
      title: 'Conecta con tu Comunidad',
      description: 'Automatiza la comunicación y crea una comunidad más comprometida y conectada',
      image: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&q=80',
      features: [
        {
          icon: Smartphone,
          title: 'App Móvil Personalizada',
          description: 'Eleva tu marca con una app móvil white-label completamente personalizada',
          link: '#app-movil'
        },
        {
          icon: MessageSquare,
          title: 'Mensajería y Directorio',
          description: 'Permite conexiones duraderas entre miembros de tu comunidad',
          link: '#mensajeria'
        },
        {
          icon: Bell,
          title: 'Feed de Comunidad',
          description: 'Transmite anuncios y actualizaciones directamente a tu comunidad',
          link: '#feed'
        },
        {
          icon: Shield,
          title: 'Gestión de Visitas',
          description: 'Optimiza la gestión de visitantes y entregas de forma segura',
          link: '#visitas'
        }
      ]
    },
    {
      id: 'grow',
      title: 'Escala tu Negocio',
      description: 'Desbloquea nuevos niveles de crecimiento y eficiencia haciendo más con menos',
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80',
      features: [
        {
          icon: Globe,
          title: 'Multi-ubicación',
          description: 'Gestiona múltiples locaciones desde un solo dashboard centralizado',
          link: '#multi-ubicacion'
        },
        {
          icon: BarChart3,
          title: 'Analytics Avanzado',
          description: 'Optimiza tus decisiones diarias con reportería en tiempo real',
          link: '#analytics'
        },
        {
          icon: TrendingUp,
          title: 'Marketplace',
          description: 'Ofrece productos y servicios adicionales disponibles para compra',
          link: '#marketplace'
        },
        {
          icon: Award,
          title: 'Programas de Beneficios',
          description: 'Promociona beneficios, descuentos y ventajas para miembros',
          link: '#beneficios'
        },
        {
          icon: Settings,
          title: 'Propiedades Personalizadas',
          description: 'Crea una experiencia personalizada para cada tipo de cliente',
          link: '#personalizacion'
        },
        {
          icon: Clock,
          title: 'Eventos y Actividades',
          description: 'Comparte y gestiona eventos desde cualquier dispositivo',
          link: '#eventos'
        }
      ]
    }
  ];

  const industries = [
    {
      title: 'Coworks',
      description: 'Automatiza tu espacio de coworking y escala tu negocio con nuestras poderosas herramientas.',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80',
      link: '#'
    },
    {
      title: 'Inmobiliarias',
      description: 'Con flexibilidad y personalización integradas, Cloudbase se adapta perfectamente a la gestión inmobiliaria.',
      image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&q=80',
      link: '#'
    },
    {
      title: 'Gimnasios',
      description: 'Gestión completa de membresías, clases y pagos para centros de entrenamiento.',
      image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=600&q=80',
      link: '#'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-white via-red-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl font-bold mb-6">
              <span className="block text-gray-900">Una plataforma.</span>
              <span className="block bg-gradient-to-r from-red-600 to-red-500 bg-clip-text text-transparent">
                Posibilidades Infinitas.
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-10">
              Las funcionalidades de Cloudbase están diseñadas para ayudarte a avanzar y mantenerte adelante.
            </p>
            <Button 
              size="lg" 
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg"
            >
              Ver en Acción
            </Button>
          </div>
        </div>
      </section>

      {/* Feature Categories */}
      {featureCategories.map((category) => (
        <section key={category.id} id={category.id} className="py-20 scroll-mt-20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            {/* Category Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-bold mb-4">
                {category.title}
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                {category.description}
              </p>
            </div>

            {/* Category Image */}
            <div className="mb-16">
              <img 
                src={category.image} 
                alt={category.title}
                className="w-full h-[400px] object-cover rounded-2xl shadow-2xl"
              />
            </div>

            {/* Features Grid */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {category.features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <Card 
                    key={index}
                    className="group hover:shadow-xl transition-all duration-300 cursor-pointer hover:-translate-y-1"
                  >
                    <CardContent className="p-6">
                      <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-600 transition-colors duration-300">
                        <Icon className="w-6 h-6 text-red-600 group-hover:text-white transition-colors duration-300" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        {feature.description}
                      </p>
                      <a 
                        href={feature.link}
                        className="text-red-600 hover:text-red-700 font-semibold flex items-center gap-2 group/link"
                      >
                        Leer más
                        <svg className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </a>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>
      ))}

      {/* Industries Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              Soluciones perfectamente adaptadas a tu negocio
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cada negocio es único. Por eso hemos diseñado Cloudbase de forma modular. Configura tus ajustes para posibilidades sin precedentes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <Card key={index} className="overflow-hidden group hover:shadow-2xl transition-all duration-300">
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={industry.image} 
                    alt={industry.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white">
                    {industry.title}
                  </h3>
                </div>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">{industry.description}</p>
                  <a 
                    href={industry.link}
                    className="text-red-600 hover:text-red-700 font-semibold inline-flex items-center gap-2 group/link"
                  >
                    Conoce cómo soportamos {industry.title}
                    <svg className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-red-600 to-red-500">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              ¿Listo para comenzar?
            </h2>
            <p className="text-xl text-red-50 mb-10">
              Comienza tu demo gratuito hoy. No se requiere tarjeta de crédito.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg"
                onClick={() => window.location.href = '/demo'}
              >
                Solicitar Demo Gratis
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg"
                onClick={() => window.location.href = '/demo'}
              >
                Ver Todos los Planes
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturesPage;